/**
 * Горизонтальный скролл для таблиц `.custom-data-table` без поломки sticky-шапки.
 *
 * Шапка таблицы липнет к окну (`position: sticky` в `_table.scss`), поэтому у
 * `.v-table__wrapper` не может быть `overflow-x: auto` — он создал бы свой
 * scroll-контейнер и sticky стал бы считаться относительно него. Вместо этого
 * wrapper обрезает таблицу через `overflow-x: clip` (scroll-контейнер не
 * создаётся), а горизонтальный сдвиг делается `translateX` всей `<table>`:
 * шапка и строки двигаются одним блоком, вертикальный скролл остаётся нативным.
 *
 * Источник сдвига — полоса `.table-hscroll` (обычный нативный scroller с
 * пустым спейсером ширины таблицы), прилипшая к низу экрана. Горизонтальное
 * колесо/тачпад/shift+колесо и горизонтальный свайп над таблицей
 * перенаправляются в эту полосу.
 *
 * Таблицы находятся автоматически через MutationObserver — подключать в
 * компонентах ничего не нужно.
 */
import type { App } from 'vue'

const WRAPPER_SELECTOR = '.custom-data-table .v-table__wrapper'

const cleanups = new Map<HTMLElement, () => void>()

function attach(wrapper: HTMLElement): () => void {
  const bar = document.createElement('div')
  const spacer = document.createElement('div')

  bar.className = 'table-hscroll'
  spacer.className = 'table-hscroll__spacer'
  bar.appendChild(spacer)
  wrapper.appendChild(bar)

  const getTable = () => wrapper.querySelector<HTMLTableElement>(':scope > table')

  // Футер после wrapper тоже липнет к низу экрана — полоса встаёт над ним
  const getFooter = () => wrapper.nextElementSibling as HTMLElement | null

  const updateBarOffset = () => {
    bar.style.insetBlockEnd = `${getFooter()?.offsetHeight ?? 0}px`
  }

  const applyOffset = () => {
    const table = getTable()
    if (table)
      table.style.transform = bar.scrollLeft ? `translateX(${-bar.scrollLeft}px)` : ''
  }

  const update = () => {
    const table = getTable()
    const overflow = table ? table.scrollWidth - wrapper.clientWidth : 0

    if (overflow > 0) {
      spacer.style.inlineSize = `${table!.scrollWidth}px`
      bar.hidden = false
      wrapper.style.touchAction = 'pan-y'
    }
    else {
      bar.hidden = true
      bar.scrollLeft = 0
      wrapper.style.touchAction = ''
    }
    updateBarOffset()
    applyOffset()
  }

  const onWheel = (e: WheelEvent) => {
    if (bar.hidden)
      return

    const delta = e.shiftKey ? e.deltaY || e.deltaX : e.deltaX
    if (!delta || (!e.shiftKey && Math.abs(e.deltaX) <= Math.abs(e.deltaY)))
      return

    const max = bar.scrollWidth - bar.clientWidth
    if ((delta < 0 && bar.scrollLeft <= 0) || (delta > 0 && bar.scrollLeft >= max))
      return

    e.preventDefault()
    bar.scrollLeft += delta
  }

  // Вертикальный пан остаётся за браузером (touch-action: pan-y), горизонтальный обрабатываем сами
  let touchStartX = 0
  let touchStartScroll = 0
  let touchId: number | null = null

  const onPointerDown = (e: PointerEvent) => {
    if (e.pointerType !== 'touch' || bar.hidden)
      return
    touchId = e.pointerId
    touchStartX = e.clientX
    touchStartScroll = bar.scrollLeft
  }

  const onPointerMove = (e: PointerEvent) => {
    if (e.pointerId !== touchId)
      return
    bar.scrollLeft = touchStartScroll - (e.clientX - touchStartX)
  }

  const onPointerEnd = (e: PointerEvent) => {
    if (e.pointerId === touchId)
      touchId = null
  }

  const resizeObserver = new ResizeObserver(update)

  resizeObserver.observe(wrapper)

  // Таблицу и футер Vuetify может пересоздать — следим за их заменой
  let observedTable: HTMLTableElement | null = null
  let observedFooter: HTMLElement | null = null

  const observeParts = () => {
    const table = getTable()
    const footer = getFooter()

    if (table !== observedTable) {
      if (observedTable)
        resizeObserver.unobserve(observedTable)
      observedTable = table
      if (table)
        resizeObserver.observe(table)
    }
    if (footer !== observedFooter) {
      if (observedFooter)
        resizeObserver.unobserve(observedFooter)
      observedFooter = footer
      if (footer)
        resizeObserver.observe(footer)
    }
  }

  const childObserver = new MutationObserver(() => {
    observeParts()
    update()
  })

  childObserver.observe(wrapper, { childList: true })
  if (wrapper.parentElement)
    childObserver.observe(wrapper.parentElement, { childList: true })
  observeParts()

  bar.addEventListener('scroll', applyOffset, { passive: true })
  wrapper.addEventListener('wheel', onWheel, { passive: false })
  wrapper.addEventListener('pointerdown', onPointerDown)
  wrapper.addEventListener('pointermove', onPointerMove)
  wrapper.addEventListener('pointerup', onPointerEnd)
  wrapper.addEventListener('pointercancel', onPointerEnd)

  update()

  return () => {
    resizeObserver.disconnect()
    childObserver.disconnect()
    wrapper.removeEventListener('wheel', onWheel)
    wrapper.removeEventListener('pointerdown', onPointerDown)
    wrapper.removeEventListener('pointermove', onPointerMove)
    wrapper.removeEventListener('pointerup', onPointerEnd)
    wrapper.removeEventListener('pointercancel', onPointerEnd)
    bar.remove()
  }
}

function sync() {
  cleanups.forEach((cleanup, wrapper) => {
    if (!wrapper.isConnected) {
      cleanup()
      cleanups.delete(wrapper)
    }
  })

  document.querySelectorAll<HTMLElement>(WRAPPER_SELECTOR).forEach(wrapper => {
    if (!cleanups.has(wrapper))
      cleanups.set(wrapper, attach(wrapper))
  })
}

export default function (_app: App) {
  if (typeof window === 'undefined')
    return

  let scheduled = false

  const observer = new MutationObserver(() => {
    if (scheduled)
      return
    scheduled = true
    requestAnimationFrame(() => {
      scheduled = false
      sync()
    })
  })

  observer.observe(document.body, { childList: true, subtree: true })
  sync()
}
